<template>
  <div
    v-show="isShow"
    class="mb-3"
  >
    <img
      ref="image"
      style="max-height: 400px;"
    >

    <div
      v-if="isLoading"
      class="d-flex align-items-center justify-content-center"
    >
      <b-spinner variant="primary" />
      <span class="ml-2">
        Loading...
      </span>
    </div>
    <div v-else>
      <b-form-input
        v-model="filename"
        class="my-3"
      />

      <b-input-group class="align-items-center mb-3">
        <b-form-input
          v-model.number="width"
          type="number"
        />
        <b-icon
          icon="x-lg"
          class="mx-2"
        />
        <b-form-input
          v-model.number="height"
          type="number"
        />
        <div class="image-editor-actions image-editor-actions--bg-actions">
          <button
            v-b-tooltip.hover
            class="crop"
            title="Crop to image"
            @click="onCropToImage"
          >
            <img
              src="/img/icons/cropToImage.svg"
              style="width: 25px; height: 25px;"
            >
          </button>
        </div>
      </b-input-group>

      <b-button
        variant="primary"
        :disabled="isSaving || !filename"
        @click="onSave"
      >
        {{ isSaving ? 'Saving...' : 'Save' }}
      </b-button>

      <b-button
        variant="primary"
        @click="cropperInstance.reset()"
      >
        Reset
      </b-button>

      <b-button
        variant="primary"
        @click="$emit('onCancel')"
      >
        Cancel
      </b-button>
    </div>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import Swal from 'sweetalert2';
import FileUploadService from '@frontend/services/api/file-upload';

export default {
  props: {
    src: {
      type: String,
      default: '',
    },
    isShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      cropperInstance: null,
      filename: '',
      width: 0,
      height: 0
    };
  },
  watch: {
    isShow() {
      if (this.isShow) {
        this.init();
      } else {
        this.destroy();
      }
    },
    src() {
      if (this.isShow) {
        this.destroy();
        this.init();
      }
    }
  },
  methods: {
    init() {
      const name = this.src.substring(this.src.lastIndexOf("/") + 1).split(".");
      const filename = name[0];
      const extension = name[1];
      this.filename = filename + "_cropped." + extension;

      this.isLoading = true;

      this.$refs.image.onload = async (event) => {

        await this.initCrop(this.$refs.image);

        this.isLoading = false;
      }
      this.$refs.image.src = this.src;
    },
    initCrop(imageElement) {
      return new Promise((resolve, reject) => {
        this.cropperInstance = new Cropper(imageElement, {
          autoCropArea: 1,
          zoomable: false,
          ready: () => {
            resolve();
          },
          crop: (event) => {
              this.width = Math.round(event.detail.width);
              this.height = Math.round(event.detail.height);
          }
        });
      })
    },
    destroy() {
      this.destroyCropper();
      this.cropperInstance = null;
      this.filename = '';
      this.width = 0;
      this.height = 0;
    },
    destroyCropper() {
      if (this.cropperInstance) {
        this.cropperInstance.destroy()
      }
    },
    onSave() {
      this.isSaving = true;

      this.cropperInstance.getCroppedCanvas().toBlob((blob) => {
        this.$emit('onSave', { blob, fileName: this.filename });
        this.$emit('onCancel');
        this.isSaving = false;
      });
    },
    async onCropToImage() {
      try {
        const isFileImage = this.$parent.isFileImage;
        
        if (isFileImage) {
          const result = await Swal.fire({
            title: 'Crop Image Options',
            text: 'How would you like to handle the image cropping?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Replace Existing',
            denyButtonText: 'Copy and Replace',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#209c86',
            denyButtonColor: '#209c86',
          });

          if (result.isConfirmed || result.isDenied) {
            const isCopyAndReplace = result.isDenied;
            
            if (result.isConfirmed) {
              const confirmReplace = await Swal.fire({
                title: "Are you sure?",
                text: "This will take effect immediately. You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, replace it!",
              });
              
              if (!confirmReplace.isConfirmed) {
                return;
              }
            }
            
            await this.processCropToImage(isCopyAndReplace);
          }
        } else {
          await this.processCropToImage(false);
        }
      } catch (error) {
        console.error('Error in crop image dialog:', error);
        toastr.error('Something went wrong with the crop image dialog');
      }
    },
    async processCropToImage(isCopyAndReplace) {
      try {
        toastr.info(
          `<div class="d-inline-flex align-content-center">Auto cropping image...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 0,
            tapToDismiss: false,
            closeButton: false
          }
        );

        const filePath = new URL(this.src).searchParams.get('file');
        const isFileImage = this.$parent.isFileImage;

        const { data } = await FileUploadService.cropImage({
          filePath: filePath,
          isFileImage: isFileImage
        });

        this.$emit('onAutoCrop', {
          imgUrl: data.imgUrl,
          filePath,
          fileName: data.fileName,
          isCopyAndReplace: isCopyAndReplace,
          isCropToImage: true
        });

        this.$emit('onCancel');
        toastr.clear();
      } catch (error) {
        console.error('Error cropping image:', error);
        toastr.clear();
        toastr.error('Something went wrong while cropping the image');
      }
    }
  }
}
</script>
